/**
 * LicenseMedia Component
 */

import * as actions from '../../../../../redux/actions/license';
import { isLicenseUsageValid, usageToString } from '../../../../../redux/helpers/license';
import {
  contextualParamMapping,
  excludedMediaType,
  mediaTypeDefinitions,
  programmingMedia,
} from '../../../../organisms/forms/LicenseBuilder/config';
import { MediumItemAnimations, UsageItemAnimations } from './animations';
import './styles.scss';

import * as classNames from 'classnames';
import { find, sortBy } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { VelocityTransitionGroup } from 'velocity-react';

/* tslint:disable-next-line */
import 'velocity-animate/velocity.ui';

import Button from '../../../../atoms/buttons/Button';
import {
  IconArrowLeft,
  IconCheck,
  IconEdit,
  IconTrash,
} from '../../../../atoms/images/Icon';
import Heading from '../../../../atoms/text/Heading';
import { Span } from '../../../../atoms/text/Label';
import { getContextualParam } from '../../../../organisms/forms/LicenseBuilder';
import { MediaButton } from './MediaButton';

type LicenseComponentEdge = GQL.IMediumEdge;
type LicenseComponentNode = GQL.IMedium;

interface IOwnStateProps {
  editingUsageIndex: number | null;
  isLicenseValid: boolean;
  selectedMediaList: ILicenseUsage[];
}

interface IOwnDispatchProps {
  addMediumToLicense(component: ILicenseComponent, contextualParamType: LicenseComponentType): void;
  setEditingUsageIndex(index: number | null): void;
  removeMediumFromLicense(id: string): void;
}

interface IOwnProps extends ILicenseComponentProps<LicenseComponentEdge> {
  onSaveTerms(): void;
}

interface IOwnState {
  selectedMedia: LicenseMediaTypeIdentifier[];
}

type LicenseMediaProps = IOwnProps & IOwnStateProps & IOwnDispatchProps;

const classes = new BEM('license-builder__step');

class LicenseMedia extends React.Component<LicenseMediaProps, IOwnState> {
  constructor(props: LicenseMediaProps) {
    super(props);

    this.state = {
      selectedMedia: props.selectedMediaList.map(({ medium }) => medium.id as LicenseMediaTypeIdentifier),
    };
  }

  componentWillReceiveProps({ selectedMediaList }: LicenseMediaProps) {
    if (selectedMediaList.length !== this.props.selectedMediaList.length) {
      const selectedMedia = selectedMediaList.map(({ medium }) => medium.id as LicenseMediaTypeIdentifier);

      if (this.props.editingUsageIndex !== null && selectedMediaList.length < this.props.selectedMediaList.length) {
        const editingUsageIndex = selectedMedia.length === 0 ?
          null : 0;

        this.onSelectUsage(editingUsageIndex);
      }

      this.setState({ selectedMedia });
    }
  }

  onSelectMedia = (component: ILicenseComponent) => {
    const contextualParam = getContextualParam(component.id as LicenseMediaTypeIdentifier)(contextualParamMapping);
    this.props.addMediumToLicense(component, contextualParam.type);
  }

  onSelectUsage(index: number | null) {
    this.props.setEditingUsageIndex(index);
  }

  renderMediaButton({ internal_id, name }: LicenseComponentNode) {
    const id = internal_id! as LicenseMediaTypeIdentifier;
    const { label, icon, hint } = find(mediaTypeDefinitions, { id })!;
    return (
      <MediaButton
        onClick={this.onSelectMedia}
        hint={hint}
        id={id}
        icon={icon}
        name={label || name}
      />
    );
  }

  renderUsage(id: LicenseMediaTypeIdentifier, index: number) {
    const { label, icon } = find(mediaTypeDefinitions, { id })!;

    let name = '';
    this.props.data.forEach(({ node }) => {
      if (node!.internal_id === id) {
        name = node!.name;
      }
    });

    const usage = this.props.selectedMediaList[index];
    const isValid = isLicenseUsageValid(usage);
    const isActive = this.props.editingUsageIndex === index;
    const className = classNames(
      { 'is-active': isActive },
      { 'is-valid': isValid },
    );

    return (
      <li
        className={className}
        key={`usage-${id}-${index}`}
        onClick={() => this.onSelectUsage(index)}
      >
        <Button
          className="usage-button"
          icon={icon}
          label={
            <Span
              maxTextLength={isActive ? -1 : 18}
            >{label || name} <small>{usageToString(usage, true)}</small>
            </Span>
          }
          size="medium"
        />
        <Button
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            this.props.removeMediumFromLicense(usage.id!);
          }}
          className="remove-usage-button"
          icon={<IconTrash />}
          size="small"
          variant="icon"
          dangerous
        />
        {!isActive && <Button
          className="edit-usage-button"
          colorScheme="light"
          icon={<IconEdit />}
          label="Edit"
          size="small"
        />}
        {isValid && (
          <div className="usage-is-valid-indicator"><IconCheck /></div>
        )}
      </li>
    );
  }

  render() {
    const {
      selectedMediaList,
    } = this.props;

    const hasUsages = !!selectedMediaList.length;
    const hasSelectedUsage = this.props.editingUsageIndex !== null;

    const extraClasses = classNames(
      { 'has-selected-usage': hasSelectedUsage },
    );

    const selectedMediaListClasses = classNames(
      'selected-media-list',
      { 'has-medias': hasUsages },
    );

    const data = sortBy(this.props.data, (a) => {
      return mediaTypeDefinitions.findIndex((val) => val.id === a.node!.internal_id);
    });

    return (
      <div {...classes('', 'media', extraClasses) }>
        <div className="media-list">
          <Heading is="h4" {...classes('title')}>Add Media:</Heading>
          <Button
            className="sample-license-button"
            hyperLinkProps={{
              href: 'https://assets.boppermusic.com/docs/Bopper_Sample_License.pdf',
              target: '_blank',
            }}
            wrapperComponent="a"
            colorScheme="black-outline"
            variant="pill"
            size="small"
            label="Preview License"
          />
          <Heading is="h5" {...classes('subtitle') }>Advertising</Heading>
          <VelocityTransitionGroup
            component="ul"
            enter={{ animation: MediumItemAnimations.In }}
            leave={{ animation: MediumItemAnimations.Out, delay: 150 }}
          >
            {data.map(({ node }) => this.shouldDisplayMediaType(node!.internal_id!) &&
              !this.isProgrammingMediaType(node!.internal_id!) && (
              <li key={node!.internal_id!}>
                {this.renderMediaButton(node!)}
              </li>
            ))}
          </VelocityTransitionGroup>
          <Heading is="h5" {...classes('subtitle') }>Programming</Heading>
          <VelocityTransitionGroup
            component="ul"
            enter={{ animation: MediumItemAnimations.In }}
            leave={{ animation: MediumItemAnimations.Out, delay: 150 }}
          >
            {data.map(({ node }) => this.shouldDisplayMediaType(node!.internal_id!) &&
              this.isProgrammingMediaType(node!.internal_id!) && (
              <li key={node!.internal_id!}>
                {this.renderMediaButton(node!)}
              </li>
            ))}
          </VelocityTransitionGroup>
        </div>
        <div className={selectedMediaListClasses}>
          {hasUsages && <Heading is="h4" {...classes('title') }>Edit Terms:</Heading>}
          <VelocityTransitionGroup
            component="ul"
            enter={{ animation: UsageItemAnimations.In, delay: 150 }}
            leave={{ animation: UsageItemAnimations.Out }}
          >
            {this.props.selectedMediaList.map(({ medium }, index) =>
              this.renderUsage(medium.id as LicenseMediaTypeIdentifier, index),
            )}
          </VelocityTransitionGroup>
          <div className="global-actions">
            {hasSelectedUsage && <Button
              onClick={() => this.onSelectUsage(null)}
              className="add-more-media-button"
              label="Add more media"
              icon={<IconArrowLeft />}
              colorScheme="light"
            />}
          </div>
        </div>
      </div>
    );
  }

  private shouldDisplayMediaType(id: number) {
    return !this.isMediaTypeExcluded(id);
  }

  private isProgrammingMediaType(id: number) {
    return programmingMedia.indexOf(id) > -1 || id === 2011;
  }

  private isMediaTypeExcluded(id: number) {
    return excludedMediaType.indexOf(id as LicenseMediaTypeIdentifier) > -1;
  }
}

const ConnectedLicenseMedia = connect(
  ({ license: { editingUsageIndex, isValid, usages } }: IStoreState): IOwnStateProps => ({
    editingUsageIndex,
    isLicenseValid: isValid,
    selectedMediaList: usages,
  }),
  (dispatch): IOwnDispatchProps => ({
    addMediumToLicense: (medium, contextualParamType) => {
      dispatch(actions.addMedium({ medium, contextualParamType }));
    },
    removeMediumFromLicense: (id) => {
      dispatch(actions.removeMedium({ id }));
    },
    setEditingUsageIndex: (index) => {
      dispatch(actions.setEditingUsageIndex({ index }));
    },
  }),
)(LicenseMedia);

export default ConnectedLicenseMedia;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/molecules/forms/license-builder/SelectMedia/index.tsx